import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { AppThunk } from 'store/store';
import { ApiErrors, Loaders } from 'api/types';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from 'features/axiosInstance';

const documentsApiPrefix = '/documents';

export type DocumentLink = {
  link_id: string;
  link_type: string;
};

export type Document = {
  can_edit: boolean;
  created_at: string;
  extension: string;
  id: string;
  links: DocumentLink[];
  name: string;
  updated_at: string;
};

export type DocumentsState = {
  apiErrors: ApiErrors;
  document: Document | null;
  downloadError: string;
  loaders: Loaders;
};

const initialState: DocumentsState = {
  apiErrors: {},
  downloadError: '',
  loaders: {},
  document: null
};

export const linkToResource = createAsyncThunk<
  Document,
  { added: string[]; documentId: string; linkType: string; removed: string[] },
  { rejectValue: string }
>('documentsLink/linkToResource', async ({ added, documentId, linkType, removed }, { rejectWithValue }) => {
  try {
    const body = {
      added,
      link_type: linkType,
      removed
    };
    const response = await axiosInstance.put(`${documentsApiPrefix}/${documentId}/links`, body);
    const { data } = response;
    return data;
  } catch (err) {
    if (!err.response) {
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
    const { status } = err.response;
    switch (status) {
      case FORBIDDEN:
      case UNAUTHORIZED:
        return rejectWithValue(i18n.t('errors.you cannot rename this document'));
      case NOT_FOUND:
        return rejectWithValue(i18n.t('errors.document not found'));
      case UNPROCESSABLE_ENTITY:
        return rejectWithValue(i18n.t('errors.invalid document name'));
      default:
        return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
});

export const fetchDocument = createAsyncThunk<Document, string, { rejectValue: string }>(
  'documentsLink/getDocument',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${documentsApiPrefix}/${id}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('errors.you cannot rename this document'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('errors.document not found'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('errors.invalid document name'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    }
  }
);

/* : (state, { payload }: PayloadAction<Document>) => {
  state.document = payload;
}, */

const documentsLinkSlice = createSlice({
  initialState,
  name: 'documentsLink',
  reducers: {
    setIsSaving: (state, { payload }: PayloadAction<boolean>) => {
      state.loaders.isSaving = payload;
    },
    setDownloadError: (state, { payload }: PayloadAction<string>) => {
      state.downloadError = payload;
    },
    resetDocument: (state: DocumentsState) => {
      state.document = null;
    },
    closeError: state => {
      state.apiErrors = {};
    }
  },
  extraReducers: builder =>
    builder
      .addCase(linkToResource.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
      })
      .addCase(linkToResource.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(linkToResource.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(fetchDocument.fulfilled, (state, { payload }) => {
        state.document = payload;
        state.loaders.isLoading = false;
      })
      .addCase(fetchDocument.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchDocument.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = documentsLinkSlice;
export { reducer as documentsLink };
export const { closeError, resetDocument, setIsSaving, setDownloadError } = actions;

export const getDocumentToLink = (state: RootState) => state.documentsLink.document;
export const getDocumentLinkLoaders = (state: RootState) => state.documentsLink.loaders;
export const getDocumentLinkApiErrors = (state: RootState) => state.documentsLink.apiErrors;
